import apiClient from './apiClient';

export const zoneService = {
  // Get all zones.
  // per_page is forced because /zones defaults to 10 and both callers here want
  // the complete set: ProductForm renders one rate box per zone, and ZoneList
  // paginates client-side over whatever it receives. Without this, the 11th zone
  // is invisible in both screens: and saving a product used to delete its rate.
  getAllZones: async (params = {}) => {
    try {
      const response = await apiClient.get('/zones', {
        params: { per_page: 100, ...params },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Cities in a zone: the content of a destination zone
  getZoneCities: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/zones/${id}/cities`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Move cities into this zone (a city belongs to exactly one zone).
  //
  // Without `confirm`, the API answers 409 with `requires_confirmation` and the
  // list of cities that already belong elsewhere, so the caller can ask before
  // repricing another zone's destinations. That response is returned rather than
  // thrown, because it is an expected answer, not a failure.
  assignCities: async (id, cityIds, confirm = false) => {
    try {
      const response = await apiClient.post(`/zones/${id}/cities`, {
        city_ids: cityIds,
        ...(confirm ? { confirm: true } : {}),
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.requires_confirmation) {
        return error.response.data;
      }
      throw error.response?.data || error.message;
    }
  },

  // Unassign a city: it becomes unpriceable until given a zone
  removeCity: async (id, cityId) => {
    try {
      const response = await apiClient.delete(`/zones/${id}/cities/${cityId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single zone
  getZone: async (id) => {
    try {
      const response = await apiClient.get(`/zones/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new zone
  createZone: async (zoneData) => {
    try {
      const response = await apiClient.post('/zones', zoneData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update zone
  updateZone: async (id, zoneData) => {
    try {
      const response = await apiClient.put(`/zones/${id}`, zoneData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete zone
  // Without `confirm`, a zone that still holds cities or rates answers 409 with
  // `requires_confirmation` and the impact counts, so the caller can spell out
  // what will be stranded. Returned rather than thrown: it is an expected answer.
  deleteZone: async (id, confirm = false) => {
    try {
      const response = await apiClient.delete(`/zones/${id}`, {
        params: confirm ? { confirm: 1 } : {},
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.requires_confirmation) {
        return error.response.data;
      }
      throw error.response?.data || error.message;
    }
  },

  // Get zone statistics
  getZoneStats: async () => {
    try {
      const response = await apiClient.get('/zones-data/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get available regions for dropdown
  getRegions: async () => {
    try {
      const response = await apiClient.get('/zones-data/regions');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Validate region pairs
  validateRegionPairs: async (regionPairs, zoneId = null) => {
    try {
      const response = await apiClient.post('/zones-data/validate-region-pairs', {
        region_pairs: regionPairs,
        zone_id: zoneId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
}; 